import {isPayrollOperationPending} from 'app/services/operation-conflict';
import {ServerErrorResponse} from 'app/services/server-error-code';
import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {Report} from '../models/report.model';
import {selectMixedGroupReportRows} from '../services/deal-payroll-group-report.adapter';
import {dealPayrollMonthFromRange, ReportService} from '../services/report.service';
import {HTTP_STATUS_CODES} from '../shared.constants/all-constants';
import {CALENDAR_MONTH_OFFSET, DATE_FORMAT, EMPTY_GROUP_ID, REPORT_DATE_PREFIX,
  REPORT_DATE_SEPARATOR} from './report-datepicker.constants';
import {appendFormattedReports} from './report-datepicker-report.mapper';
import {ReportDatepickerReportPresenter} from './report-datepicker-report.presenter';
import {ReportProgressCoordinator} from './report-progress.coordinator';
import {GroupReportRequestContext, GroupReportStorageContext, ReportDatepickerReportView,
  ReportPeriod} from './report-datepicker.types';
const GROUP_REPORT_TARGET = 'group';

// ReportDatepickerComponent OnDestroy reaches the request replacement and destroy signals.
@Injectable()
export class ReportDatepickerGroupReportCoordinator {
  private activeRequest: GroupReportRequestContext = null;
  private readonly requestChanged$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(private reportService: ReportService, private progress: ReportProgressCoordinator,
    private presenter: ReportDatepickerReportPresenter) {}

  destroy(): void {
    this.activeRequest = null;
    this.destroy$.next();
    this.destroy$.complete();
    this.requestChanged$.complete();
  }

  start(view: ReportDatepickerReportView, period: ReportPeriod): void {
    this.cancel(view);
    view.isLoaded = false;
    view.dealPayrollGroupError = view.payrollOperationPending = false;
    this.reportService.reportsMin = [];
    this.reportService.filteredReports = [];
    this.reportService.allReports = [];
    this.progress.startGroup(view);
    const groupId = this.resolveGroupId(view);
    const context = {groupId: groupId, groupName: view.groupName,
      from: period.from, to: period.to, freshReportContext: this.reportService.captureFreshReportContext(
        period.from, period.to, JSON.stringify([GROUP_REPORT_TARGET, groupId]))};
    this.activeRequest = context;
    try {
      this.requestReport(view, context);
    } catch (error) {
      this.failRequest(view, context);
    }
  }

  cancel(view: ReportDatepickerReportView): void {
    if (!this.activeRequest) {
      return;
    }
    this.activeRequest = null;
    this.requestChanged$.next();
    this.reportService.reportsMin = [];
    this.progress.stop();
    view.isLoaded = true;
    view.showReportProgress = false;
  }

  private requestReport(view: ReportDatepickerReportView, context: GroupReportRequestContext): void {
    this.reportService.getReportByGroupId({groupId: context.groupId, from: context.from,
      to: context.to, progressId: this.progress.currentId()})
      .takeUntil(this.requestChanged$).takeUntil(this.destroy$).subscribe(
        response => this.processReport(view, response, context),
        error => this.failRequest(view, context, error));
  }

  private processReport(view: ReportDatepickerReportView, response: HttpResponse<Report[]>,
    context: GroupReportRequestContext): void {
    if (this.activeRequest !== context) {
      return;
    }
    if (!this.matchesCurrentSelection(view, context)) {
      this.cancel(view);
      return;
    }
    const reports = response.status === HTTP_STATUS_CODES.OK
      ? selectMixedGroupReportRows(response.body, dealPayrollMonthFromRange(context.from, context.to)) : null;
    if (!reports) {
      this.failRequest(view, context);
      return;
    }
    appendFormattedReports(this.reportService, reports);
    this.finish(view, context);
  }

  private failRequest(view: ReportDatepickerReportView, context: GroupReportRequestContext, error?: ServerErrorResponse): void {
    if (this.activeRequest !== context) {
      return;
    }
    this.activeRequest = null;
    this.reportService.reportsMin = [];
    view.payrollOperationPending = isPayrollOperationPending(error);
    view.dealPayrollGroupError = !view.payrollOperationPending;
    this.progress.fail(view);
    view.isLoaded = true;
  }

  private finish(view: ReportDatepickerReportView, context: GroupReportRequestContext): void {
    this.activeRequest = null;
    this.progress.complete(view);
    const reports: Report[] = this.reportService.reportsMin;
    this.reportService.rememberGroupPayrollReport({groupId: context.groupId,
      from: context.from, to: context.to}, reports);
    this.prepareCompactReports(reports, context);
    this.reportService.rememberFreshReport(this.reportService.reportsMin, context.freshReportContext);
    const stored: GroupReportStorageContext = {from: context.from, to: context.to,
      reportDate: this.reportService.reportDate, reports: this.reportService.reportsMin,
      groupName: context.groupName, groupId: context.groupId};
    this.presenter.showGroupReport(stored, () => view.isLoaded = true);
  }

  private prepareCompactReports(reports: Report[], period: ReportPeriod): void {
    const service = this.reportService;
    service.reportsMin = service.toCompactReports(reports, dealPayrollMonthFromRange(period.from, period.to));
    service.allReports = service.reportsMin.concat(service.filteredReports);
    service.reportDate = REPORT_DATE_PREFIX + period.from + REPORT_DATE_SEPARATOR + period.to;
  }

  private matchesCurrentSelection(view: ReportDatepickerReportView, context: GroupReportRequestContext): boolean {
    return this.resolveGroupId(view) === context.groupId && this.selectedDate(view.from) === context.from
      && this.selectedDate(view.to) === context.to;
  }

  private selectedDate(value: NgbDateStruct): string {
    return value ? moment([value.year, value.month - CALENDAR_MONTH_OFFSET, value.day]).format(DATE_FORMAT) : null;
  }

  private resolveGroupId(view: ReportDatepickerReportView): string {
    return view.groupId && view.groupId.id ? String(view.groupId.id) : EMPTY_GROUP_ID;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-datepicker-group-report.coordinator.ts